<template>
  <div
    :class="['email-component', 'line-items-component', { selected }]"
    :style="computedStyles"
    @click="handleClick"
  >
    <div class="line-items-wrapper">
      <!-- Header row -->
      <div class="line-items-header" :style="headerStyles">
        <div class="header-item">{{ getLabel('item') }}</div>
        <div class="header-quantity">{{ getLabel('quantity') }}</div>
        <div v-if="componentData.showPrice !== false" class="header-price">{{ getLabel('price') }}</div>
      </div>

      <!-- Line items -->
      <div
        v-for="(item, index) in displayItems"
        :key="index"
        class="line-item"
        :style="itemStyles"
      >
        <div class="item-details">
          <div class="item-name">{{ item.name }}</div>
          <div class="item-info" v-if="item.info">
            <div v-for="(info, infoIndex) in item.info" :key="infoIndex">
              {{ getInfoLabel(info.label) }}: {{ info.value }}
            </div>
          </div>
        </div>
        <div class="item-quantity">
          <div v-if="ticketLines(item).length > 0" class="d-inline-block text-left">
            <div v-for="(line, lineIndex) in ticketLines(item)" :key="lineIndex">{{ line }}</div>
          </div>
          <template v-else>{{ item.quantity }}</template>
        </div>
        <div v-if="componentData.showPrice !== false" class="item-price">{{ formatPrice(item.price) }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import { pickerTimeFormat } from '@/plugins/dates'

export default {
  name: 'LineItemsComponent',
  props: ['componentData', 'selected'],
  data() {
    return {
      labels: {
        english: {
          item: 'ITEM',
          quantity: 'QUANTITY',
          price: 'PRICE',
          // Info labels
          dateTime: 'Date/time',
          bookingReference: 'Booking reference',
          code: 'Code',
          orderNumber: 'Order number',
          reference: 'Reference',
          validUntil: 'Valid until',
          location: 'Location',
          staff: 'Staff',
          duration: 'Duration',
          notes: 'Notes'
        },
        spanish: {
          item: 'ARTÍCULO',
          quantity: 'CANTIDAD',
          price: 'PRECIO',
          // Info labels
          dateTime: 'Fecha/hora',
          bookingReference: 'Referencia de reserva',
          code: 'Código',
          orderNumber: 'Número de orden',
          reference: 'Referencia',
          validUntil: 'Válido hasta',
          location: 'Ubicación',
          staff: 'Personal',
          duration: 'Duración',
          notes: 'Notas'
        },
        french: {
          item: 'ARTICLE',
          quantity: 'QUANTITÉ',
          price: 'PRIX',
          // Info labels
          dateTime: 'Date/heure',
          bookingReference: 'Référence de réservation',
          code: 'Code',
          orderNumber: 'Numéro de commande',
          reference: 'Référence',
          validUntil: 'Valable jusqu\'au',
          location: 'Lieu',
          staff: 'Personnel',
          duration: 'Durée',
          notes: 'Notes'
        },
        swedish: {
          item: 'ARTIKEL',
          quantity: 'ANTAL',
          price: 'PRIS',
          // Info labels
          dateTime: 'Datum/tid',
          bookingReference: 'Bokningsreferens',
          code: 'Kod',
          orderNumber: 'Ordernummer',
          reference: 'Referens',
          validUntil: 'Giltig till',
          location: 'Plats',
          staff: 'Personal',
          duration: 'Varaktighet',
          notes: 'Anteckningar'
        },
        danish: {
          item: 'VARE',
          quantity: 'ANTAL',
          price: 'PRIS',
          dateTime: 'Dato/tidspunkt',
          bookingReference: 'Bookingreference',
          code: 'Kode',
          orderNumber: 'Ordrenummer',
          reference: 'Reference',
          validUntil: 'Gyldig til',
          location: 'Sted',
          staff: 'Personale',
          duration: 'Varighed',
          notes: 'Noter'
        },
        greek: {
          item: 'ΣΤΟΙΧΕΙΟ',
          quantity: 'ΠΟΣΟΤΗΤΑ',
          price: 'ΤΙΜΗ',
          // Info labels
          dateTime: 'Ημερομηνία/ώρα',
          bookingReference: 'Αναφορά κράτησης',
          code: 'Κωδικός',
          orderNumber: 'Αριθμός παραγγελίας',
          reference: 'Αναφορά',
          validUntil: 'Ισχύει μέχρι',
          location: 'Τοποθεσία',
          staff: 'Προσωπικό',
          duration: 'Διάρκεια',
          notes: 'Σημειώσεις'
        }
      }
    }
  },
  computed: {
    currentLanguage() {
      return this.componentData.language || 'english'
    },

    computedStyles() {
      const styles = this.componentData.styles || {}
      const backgroundColor = styles.backgroundColor === 'inherit' || !styles.backgroundColor ?
        'transparent' : styles.backgroundColor

      return {
        backgroundColor: backgroundColor,
        padding: `${styles.paddingTop || '20'}px ${styles.paddingLeft || '20'}px ${styles.paddingBottom || '20'}px ${styles.paddingRight || '20'}px`,
        marginTop: `${styles.marginTop || '16'}px`,
        marginBottom: `${styles.marginBottom || '16'}px`,
        borderRadius: `${styles.borderRadius || '0'}px`,
        border: styles.showBorder ? `1px solid ${styles.borderColor || '#e5e7eb'}` : 'none',
        fontFamily: styles.fontFamily || 'Arial, sans-serif'
      }
    },

    headerStyles() {
      const styles = this.componentData.styles || {}
      const showPrice = this.componentData.showPrice !== false

      return {
        display: 'grid',
        gridTemplateColumns: showPrice ? '1fr auto auto' : '1fr auto',
        gap: '20px',
        alignItems: 'center',
        fontSize: `${styles.headerFontSize || '12'}px`,
        fontWeight: styles.headerFontWeight || '600',
        color: styles.headerColor || '#666666',
        borderBottom: `2px solid ${styles.dividerColor || '#e5e7eb'}`,
        paddingBottom: '12px',
        marginBottom: '12px',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        fontFamily: styles.fontFamily || 'Arial, sans-serif'
      }
    },

    itemStyles() {
      const styles = this.componentData.styles || {}
      const showPrice = this.componentData.showPrice !== false

      return {
        display: 'grid',
        gridTemplateColumns: showPrice ? '1fr auto auto' : '1fr auto',
        gap: '20px',
        alignItems: 'flex-start',
        fontSize: `${styles.fontSize || '14'}px`,
        color: styles.textColor || '#333333',
        borderBottom: `1px solid ${styles.dividerColor || '#f0f0f0'}`,
        paddingTop: '12px',
        paddingBottom: '12px',
        fontFamily: styles.fontFamily || 'Arial, sans-serif'
      }
    },

    displayItems() {
      // Use sample items if provided, otherwise use defaults matching the screenshot
      if (this.componentData.items && this.componentData.items.length > 0) {
        return this.componentData.items
      }

      // Default placeholder items with label keys instead of hardcoded text
      return [
        {
          name: 'My game',
          info: [
            { label: 'dateTime', value: this.sampleDateTime },
            { label: 'bookingReference', value: '#20227577' }
          ],
          quantity: 'x6',
          tickets: [{ category: 'Adult', number_of_players: 4 }, { category: 'Child', number_of_players: 2 }],
          price: '$174.00'
        }
      ]
    },

    sampleDateTime() {
      // Format the placeholder date the same way sent emails do: the group's
      // date/time formats, weekday prefix only when show_day_of_week is on.
      let group = {}
      try {
        group = JSON.parse(localStorage.getItem('group')) || {}
      } catch (e) {
        group = {}
      }
      const sample = moment('2025-08-28 11:45', 'YYYY-MM-DD HH:mm')
      const dateFormat = (group.show_day_of_week ? 'dddd, ' : '') + (group.date_format || 'MM-DD-YYYY')
      const text = sample.format(dateFormat) + ' • ' + sample.format(pickerTimeFormat(group.time_format))
      // "Show end time": the sample books 90 minutes
      return this.componentData.showEndTime === true ? text + ' - ' + sample.clone().add(90, 'minutes').format(pickerTimeFormat(group.time_format)) : text
    }
  },
  methods: {
    handleClick() {
      this.$emit('click')
    },

    ticketLines(item) {
      if (this.componentData.showTicketCategories !== true) return []
      const tickets = Array.isArray(item.tickets) ? item.tickets : []
      return tickets
        .filter(t => t && Number(t.number_of_players) > 0)
        .map(t => (t.category ? `${t.category}: ${Number(t.number_of_players)}` : String(Number(t.number_of_players))))
    },

    getLabel(key) {
      const languageLabels = this.labels[this.currentLanguage] || this.labels.english
      return languageLabels[key] || key.toUpperCase()
    },

    getInfoLabel(labelKey) {
      // If it's already a translated label (for backward compatibility), return as is
      if (labelKey.includes('/') || labelKey.includes(':')) {
        return labelKey
      }

      // Otherwise, get the translated label
      const languageLabels = this.labels[this.currentLanguage] || this.labels.english
      return languageLabels[labelKey] || labelKey
    },

    formatPrice(price) {
      // If price is already a formatted string, return as is
      if (typeof price === 'string') {
        return price
      }

      // For numeric prices, format with default currency symbol
      const formatted = price.toFixed(2)

      // Format based on language conventions
      if (this.currentLanguage === 'french') {
        // French format: 174,00 €
        const frenchFormatted = formatted.replace('.', ',')
        return `${frenchFormatted} €`
      } else if (this.currentLanguage === 'spanish') {
        // Spanish format: €174,00
        const spanishFormatted = formatted.replace('.', ',')
        return `€${spanishFormatted}`
      } else if (this.currentLanguage === 'swedish') {
        // Swedish format: 174,00 kr
        const swedishFormatted = formatted.replace('.', ',')
        return `${swedishFormatted} kr`
      } else if (this.currentLanguage === 'danish') {
        // Danish format: 174,00 kr.
        const danishFormatted = formatted.replace('.', ',')
        return `${danishFormatted} kr.`
      } else if (this.currentLanguage === 'greek') {
        // Greek format: 174,00 €
        const greekFormatted = formatted.replace('.', ',')
        return `${greekFormatted} €`
      }

      // Default English format: $174.00
      return `$${formatted}`
    }
  }
}
</script>

<style scoped>
.line-items-component {
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid transparent;
  width: 100%;
  box-sizing: border-box;
}

.line-items-component:hover,
.line-items-component.selected {
  border-color: #e14eca !important;
  box-shadow: 0 0 0 4px rgba(225, 78, 202, 0.1);
}

.line-items-wrapper {
  width: 100%;
}

.header-item {
  text-align: left;
}

.header-quantity,
.header-price {
  text-align: right;
  min-width: 100px;
}

.line-item:last-child {
  border-bottom: none !important;
}

.item-details {
  text-align: left;
}

.item-name {
  font-weight: 500;
  margin-bottom: 4px;
}

.item-info {
  font-size: 0.9em;
  line-height: 1.4;
  margin-top: 4px;
}

.item-quantity {
  text-align: right;
  min-width: 100px;
  white-space: nowrap;
}

.item-price {
  text-align: right;
  min-width: 100px;
  font-weight: 500;
  white-space: nowrap;
}

/* Responsive adjustments */
@media (max-width: 600px) {
  .line-items-header,
  .line-item {
    grid-template-columns: 1fr auto !important;
  }

  .header-quantity,
  .item-quantity {
    display: none;
  }

  .header-price,
  .item-price {
    min-width: 80px;
  }
}
</style>
